import { Card, Empty, Tooltip, Modal, Button, Input, Form, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { InsightTag } from 'src/components';
import {
  useGetTagList,
  useDeleteTag,
  useUpdateTagDetails,
} from 'src/hooks/tag';
import { Tag } from 'src/models';
import { TagEditForm } from './tagEditForm';
import { showToastSuccess } from 'src/utilities/toast';
import { showToastError } from 'src/utilities/errortoast';
import { SkeletonLoader } from '@insight-health-ai/roseate/components';
import { NonOptional } from '@insight-health-ai/roseate/utils';
import { AutoCreateTagsModal } from './autoCreateTags';
import { usePageHeader } from 'src/hooks/misc';

const AddCategoryModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: (name?: string) => void;
}) => {
  const [form] = Form.useForm();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onClose(values.name);
    } catch (error) {
      // do nothing
    }
  };
  return (
    <Modal
      open={open}
      onCancel={() => onClose()}
      title="Add Category"
      footer={
        <div className="flex justify-end gap-2 mt-8">
          <Button className="ih-button-secondary" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button className="ih-button-primary" onClick={handleSubmit}>
            Save
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" initialValues={{ name: '' }}>
        <Form.Item
          className="field-wrapper"
          name="name"
          label={null}
          rules={[{ required: true, message: 'Please enter a category name' }]}
        >
          <Input className="field" placeholder="Category Name" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const TagList = () => {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useGetTagList({ search });
  const [newCategory, setNewCategory] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const { mutateAsync: deleteTag, isPending: isDeletingTag } = useDeleteTag();
  const {
    mutateAsync: updateTag,
    isPending: isUpdatingTag,
    variables: updateTagVariables,
  } = useUpdateTagDetails();
  const [editTag, setEditTag] = useState<Tag | null>(null);
  const [newTag, setNewTag] = useState<NonOptional<
    Partial<Omit<Tag, 'id'>>,
    'category'
  > | null>(null);
  const [isAutoCreatingTags, setIsAutoCreatingTags] = useState(false);

  usePageHeader({
    title: 'Manage Tags',
  });

  const handleDeleteTag = async (tagId: Tag['id']) => {
    const { destroy } = await Modal.confirm({
      type: 'error',
      title: 'Delete Tag',
      content: 'Are you sure you want to delete this tag?',
      footer: (
        <div className="flex justify-end gap-2 mt-4">
          <Button
            className="ih-button-secondary"
            onClick={() => destroy()}
            disabled={isDeletingTag}
          >
            Cancel
          </Button>
          <Button
            className="ih-button-negative"
            onClick={async () => {
              try {
                await deleteTag({ tagId });
                showToastSuccess('Tag deleted successfully!');
                destroy();
              } catch (error: any) {
                showToastError(
                  error?.response?.data?.message ||
                    'Failed to delete tag. Please try again.'
                );
              }
            }}
            loading={isDeletingTag}
            disabled={isDeletingTag}
          >
            Delete
          </Button>
        </div>
      ),
    });
  };

  const renderChildren = () => {
    if (isLoading) {
      return (
        <>
          <Card>
            <SkeletonLoader />
          </Card>
          <Card>
            <SkeletonLoader />
          </Card>
        </>
      );
    }

    const categories = Object.entries({
      ...(newCategory && !search && { [newCategory]: [] }),
      ...data?.groupedTags,
    });

    if (!categories.length) {
      return <Empty description="No tags found" />;
    }

    return (
      <div className="flex flex-col gap-4">
        {categories.map(([category, tags]) => {
          return (
            <Card
              title={
                <div className="flex items-center gap-2 justify-between w-full">
                  <span>{category}</span>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => setNewTag({ category })}
                  >
                    Add Tag
                  </Button>
                </div>
              }
              key={category}
            >
              <div className="flex flex-col gap-3">
                {tags?.length ? (
                  tags.map((tag) => {
                    const isUpdating =
                      isUpdatingTag && updateTagVariables?.id === tag.id;
                    return (
                      <div
                        key={tag.id}
                        className={`flex items-center gap-2 ${
                          isUpdating ? 'opacity-80' : ''
                        }`}
                      >
                        <InsightTag tag={tag} isSelected />
                        <div
                          className="ml-auto flex items-center gap-2"
                          style={
                            isUpdating
                              ? {
                                  pointerEvents: 'none',
                                  cursor: 'not-allowed',
                                }
                              : {}
                          }
                        >
                          <Tooltip
                            title={tag.is_active ? 'Deactivate' : 'Activate'}
                          >
                            <Switch
                              size="small"
                              checked={tag.is_active}
                              loading={isUpdating}
                              onChange={async (value) => {
                                await updateTag({
                                  id: tag.id,
                                  is_active: value,
                                });
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="Edit tag">
                            <EditOutlined onClick={() => setEditTag(tag)} />
                          </Tooltip>
                          <Tooltip title="Delete tag">
                            <DeleteOutlined
                              onClick={() => handleDeleteTag(tag.id)}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div className="flex flex-col gap-2 items-center">
                        <span className="text-sm text-gray-500">
                          Add a tag to this category
                        </span>
                        <Button
                          className="ih-button-primary w-fit"
                          onClick={() => setNewTag({ category })}
                        >
                          Add Tag
                        </Button>
                      </div>
                    }
                  />
                )}
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div
      style={{ height: 'calc(100dvh - 100px)' }}
      className="page-wrapper care-connect-page"
    >
      <div className="flex flex-col gap-4 max-w-[640px] pb-6">
        <div className="flex items-center gap-2">
          <Input
            size="large"
            placeholder="Search tags"
            allowClear
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
          <Button
            className="ih-button-primary"
            onClick={() => setIsAddingCategory(true)}
          >
            Add Category
          </Button>
          <Button
            className="ih-button-primary"
            onClick={() => setIsAutoCreatingTags(true)}
          >
            ✨ Auto Create
          </Button>
        </div>
        {renderChildren()}
      </div>
      {(editTag || newTag) && (
        <TagEditForm
          open
          onClose={(success) => {
            setEditTag(null);
            setNewTag(null);
            if (success) {
              setNewCategory('');
            }
          }}
          tag={editTag || newTag || { category: newCategory }}
        />
      )}
      {isAddingCategory && (
        <AddCategoryModal
          open
          onClose={(name) => {
            setIsAddingCategory(false);
            if (name) {
              setNewCategory(name || '');
            }
          }}
        />
      )}
      {isAutoCreatingTags && (
        <AutoCreateTagsModal
          open
          onClose={() => setIsAutoCreatingTags(false)}
        />
      )}
    </div>
  );
};
